import { 
  Newspaper, Users, Trophy, Calendar, Briefcase, LayoutGrid, Rocket,
  GraduationCap, CircleDot, Lightbulb, Building2, HandshakeIcon, TrendingUp,
  FileText, Mic, Shield, BookOpen, Brain, Target, Star, Ticket, User, Globe,
  ShoppingBag, Sparkles, Bot, Heart, Video, Award, Eye, DollarSign, Landmark,
  BookMarked, Home, Compass, Network, Banknote, GraduationCap as Cap,
  Clock, Plus, Play, MessageCircle, Presentation, PenTool, Crown,
  LayoutList, Quote,
  type LucideIcon
} from "lucide-react";

export interface NavMenuItem {
  label: string;
  href: string;
  icon?: LucideIcon;
  description?: string;
  keywords?: string[];
}

export interface NavMenuSection {
  title?: string;
  items: NavMenuItem[];
}

export interface SubNavItem {
  label: string;
  href: string;
  icon?: LucideIcon;
  badge?: string;
  isNew?: boolean;
  requiredRole?: string;
  priorityRole?: string;
  interestTags?: string[];
  priority?: number;
  keywords?: string[];
}

export type Section = "discover" | "network" | "capital" | "programs" | "events" | "membership" | "summit";

export interface SectionConfig {
  label: string;
  href: string;
  icon: LucideIcon;
  description: string;
  subNavItems: SubNavItem[];
  hamburgerGroups: NavMenuSection[];
  routePatterns: string[];
}

export const sections: Record<Section, SectionConfig> = {
  discover: {
    label: "Discover",
    href: "/media",
    icon: Compass,
    description: "Articles, news, video, podcasts, and publications",
    subNavItems: [
      { label: "All", href: "/media", icon: Sparkles },
      { label: "Articles", href: "/media/articles", icon: FileText },
      { label: "News", href: "/media/news", icon: Newspaper },
      { label: "Video", href: "/videos", icon: Video, isNew: true },
      { label: "Daily Brief", href: "/media/daily-brief", icon: Brain },
      { label: "Trending", href: "/media/trending", icon: TrendingUp },
      { label: "Podcasts", href: "/podcasts", icon: Mic },
      { label: "Magazine", href: "/chief-in-tech-magazine", icon: BookMarked },
      { label: "Storylines", href: "/storylines", icon: Mic },
      { label: "Books", href: "/books", icon: BookOpen },
      { label: "Topics", href: "/media/topics", icon: LayoutGrid },
      { label: "100 Leaders", href: "/leaders", icon: Trophy, keywords: ["100", "leaders", "cit100", "top", "executive", "list", "prominent"] },
      { label: "Companies", href: "/companies", icon: Building2 },
    ],
    hamburgerGroups: [
      {
        title: "Read",
        items: [
          { label: "All Articles", href: "/media/articles", icon: FileText, description: "Browse all content" },
          { label: "News Hub", href: "/media/news", icon: Newspaper, description: "Industry news, funding & executive moves" },
          { label: "Trending", href: "/media/trending", icon: TrendingUp, description: "Most popular content" },
          { label: "Daily Brief", href: "/media/daily-brief", icon: Brain, description: "AI-curated news digest" },
        ]
      },
      {
        title: "Watch & Listen",
        items: [
          { label: "Videos", href: "/videos", icon: Video, description: "Watch video content" },
          { label: "Podcasts", href: "/podcasts", icon: Mic, description: "Listen to podcasts" },
          { label: "Storylines", href: "/storylines", icon: Mic, description: "Live event coverage" },
        ]
      },
      {
        title: "Publications",
        items: [
          { label: "Magazine", href: "/chief-in-tech-magazine", icon: BookMarked, description: "Chief in Tech Magazine" },
          { label: "100 Leaders", href: "/leaders", icon: Trophy, description: "Top tech executives", keywords: ["100", "leaders", "cit100", "top", "executive", "list"] },
          { label: "Companies", href: "/companies", icon: Building2, description: "Top companies to watch" },
          { label: "The Book", href: "/book", icon: BookOpen, description: "Break the Silicon Ceiling" },
          { label: "Book Features", href: "/books", icon: BookOpen, description: "Featured book reviews" },
        ]
      },
    ],
    routePatterns: [
      "/media", "/articles", "/videos", "/podcasts", "/magazine", "/chief-in-tech-magazine",
      "/book", "/books", "/storylines", "/daily-brief", "/companies", "/leaders",
    ],
  },

  network: {
    label: "Network",
    href: "/network",
    icon: Users,
    description: "Directory, jobs, and peer connections",
    subNavItems: [
      { label: "Directory", href: "/directory", icon: Users },
      { label: "AI Networking", href: "/networking", icon: Sparkles, priorityRole: "executive", priority: 1, interestTags: ["connect_leaders", "specific_intro"] },
      { label: "Peer Circles", href: "/circles", icon: CircleDot, priorityRole: "executive", priority: 2, interestTags: ["peer_exchange", "hot_seat", "mentor_founders"] },
      { label: "Executive Search", href: "/executive-search", icon: Brain, priorityRole: "executive", priority: 3, interestTags: ["exec_job_search", "recruiter_support"] },
      { label: "Jobs", href: "/jobs", icon: Briefcase, interestTags: ["exec_job_search", "advance_career"] },
      { label: "Board Opportunities", href: "/board-opportunities", icon: Award, interestTags: ["advance_career"] },
    ],
    hamburgerGroups: [
      {
        title: "People",
        items: [
          { label: "All Profiles", href: "/directory", icon: Users, description: "Browse full directory" },
          { label: "Peer Circles", href: "/circles", icon: CircleDot, description: "Peer learning groups" },
          { label: "AI Networking", href: "/networking", icon: Sparkles, description: "AI-powered introductions" },
          { label: "EWITA Chat", href: "/ewita-chat", icon: Bot, description: "AI networking assistant" },
        ]
      },
      {
        title: "Events",
        items: [
          { label: "All Events", href: "/events", icon: Calendar, description: "Browse all events" },
          { label: "Executive Forums", href: "/events/executive-forum", icon: Calendar, description: "Exclusive networking forums" },
          { label: "Speakers", href: "/speakers", icon: Mic, description: "Featured speakers" },
        ]
      },
      {
        title: "Opportunities",
        items: [
          { label: "Jobs", href: "/jobs", icon: Briefcase, description: "Executive career opportunities" },
          { label: "Executive Search", href: "/executive-search", icon: Brain, description: "AI-powered talent matching" },
          { label: "Board Match", href: "/board-match", icon: LayoutGrid, description: "Board placement" },
          { label: "Board Opportunities", href: "/board-opportunities", icon: Award, description: "Board seat listings" },
        ]
      },
    ],
    routePatterns: [
      "/directory", "/network", "/jobs", "/executive-search",
      "/board-match", "/board-opportunities", "/circles", "/networking", "/ewita-chat",
      "/profile", "/bio",
    ],
  },

  capital: {
    label: "Capital",
    href: "/capital",
    icon: Landmark,
    description: "Fundraising, investing, and deal flow",
    subNavItems: [
      { label: "Overview", href: "/capital", icon: Landmark },
      { label: "Workspace", href: "/founder/workspace", icon: Rocket, requiredRole: "founder", priority: 1, interestTags: ["launch_startup"] },
      { label: "Deal Readiness", href: "/capital/deal-readiness", icon: Target, requiredRole: "founder", priority: 2, interestTags: ["raise_funding", "exit_readiness"] },
      { label: "Dealroom", href: "/capital/dealroom", icon: HandshakeIcon, requiredRole: "investor", priority: 1, interestTags: ["invest_startups"] },
      { label: "Fund", href: "/capital/fund", icon: DollarSign, interestTags: ["invest_startups", "lp_gp_discovery"], keywords: ["venture", "fund", "seed", "lp", "gp", "invest", "5m"] },
      { label: "Portfolio Hub", href: "/capital/portfolio-hub", icon: TrendingUp },
      { label: "Funding Tracker", href: "/funding", icon: Rocket, interestTags: ["raise_funding", "invest_startups"] },
      { label: "Investor Apply", href: "/capital/investor-apply", icon: HandshakeIcon, requiredRole: "investor", priority: 2, interestTags: ["invest_startups"] },
    ],
    hamburgerGroups: [
      {
        title: "Founders",
        items: [
          { label: "Founder Workspace", href: "/founder/workspace", icon: Rocket, description: "Your startup hub" },
          { label: "Deal Readiness", href: "/capital/deal-readiness", icon: Target, description: "Raise preparation checklist" },
          { label: "One-Page Brief", href: "/founder/one-page-brief", icon: FileText, description: "Pitch document builder" },
          { label: "Investor Updates", href: "/founder/investor-updates", icon: TrendingUp, description: "Share progress" },
          { label: "Portfolio Hub", href: "/capital/portfolio-hub", icon: TrendingUp, description: "Manage your deals" },
        ]
      },
      {
        title: "Investors",
        items: [
          { label: "Dealroom", href: "/capital/dealroom", icon: HandshakeIcon, description: "Investment opportunities" },
          { label: "Apply as Investor", href: "/capital/investor-apply", icon: HandshakeIcon, description: "Get dealroom access" },
          { label: "Investor Network", href: "/investor-network", icon: Users, description: "Exclusive investor clubs" },
          { label: "Portfolio Hub", href: "/capital/portfolio-hub", icon: TrendingUp, description: "Track investments" },
        ]
      },
      {
        title: "Fund & More",
        items: [
          { label: "Chief in Tech Capital", href: "/capital/fund", icon: DollarSign, description: "$5M pre-seed and seed venture fund", keywords: ["venture", "fund", "seed", "lp", "gp", "invest", "5m", "capital"] },
          { label: "Funding Rounds", href: "/funding", icon: Rocket, description: "Browse funding activity" },
          { label: "Risk Factors", href: "/capital/risk-factors", icon: Shield, description: "Investment disclosures" },
        ]
      },
    ],
    routePatterns: [
      "/capital", "/founder", "/funding", "/investor-network", "/dealroom",
      "/startup-benefits",
    ],
  },

  programs: {
    label: "Programs",
    href: "/programs",
    icon: GraduationCap,
    description: "Academy, cohorts, and community programs",
    subNavItems: [
      { label: "Academy", href: "/academy", icon: GraduationCap, interestTags: ["take_courses", "develop_leadership"] },
      { label: "Cohorts", href: "/cohorts/apply", icon: Rocket, priorityRole: "founder", priority: 1, interestTags: ["peer_exchange", "develop_leadership"] },
      { label: "Challenges", href: "/challenges", icon: Lightbulb, interestTags: ["corporate_innovation"] },
      { label: "Get Involved", href: "/get-involved", icon: Heart },
      { label: "Contribute", href: "/contribute", icon: FileText, priorityRole: "contributor", priority: 1, interestTags: ["share_content"] },
      { label: "Nominate", href: "/nominate", icon: Award },
    ],
    hamburgerGroups: [
      {
        title: "Learn",
        items: [
          { label: "Academy", href: "/academy", icon: GraduationCap, description: "Leadership courses" },
          { label: "Cohort Programs", href: "/cohorts/apply", icon: Rocket, description: "Apply to cohorts" },
        ]
      },
      {
        title: "Join",
        items: [
          { label: "Innovation Challenges", href: "/challenges", icon: Lightbulb, description: "Corporate partnerships" },
          { label: "Peer Circles", href: "/circles", icon: CircleDot, description: "Peer learning groups" },
          { label: "Ask the Board", href: "/community/ask-board", icon: Users, description: "Expert Q&A" },
        ]
      },
      {
        title: "Contribute",
        items: [
          { label: "Get Involved", href: "/get-involved", icon: Heart, description: "Join our community" },
          { label: "Write for Us", href: "/contribute", icon: FileText, description: "Submit content" },
          { label: "Nominate a Leader", href: "/nominate", icon: Award, description: "Nominate for 100 Leaders" },
        ]
      },
    ],
    routePatterns: [
      "/programs", "/academy", "/cohorts", "/get-involved", "/contribute", "/nominate",
      "/community", "/challenges",
    ],
  },

  events: {
    label: "Events",
    href: "/events",
    icon: Calendar,
    description: "Summit, forums, salons, and partner events",
    subNavItems: [
      { label: "All Events", href: "/events", icon: Calendar },
      { label: "Executive Forums", href: "/events/executive-forum", icon: Award, interestTags: ["connect_leaders", "speaking_media"] },
      { label: "CiT Summit", href: "/events/chief-in-tech-summit", icon: Globe },
      { label: "Speakers", href: "/speakers", icon: Mic, interestTags: ["speaking_media"] },
    ],
    hamburgerGroups: [
      {
        title: "Our Events",
        items: [
          { label: "All Events", href: "/events", icon: Calendar, description: "Browse all events" },
          { label: "Executive Forums", href: "/events/executive-forum", icon: Award, description: "Invitation-only executive forums" },
          { label: "Chief in Tech Summit", href: "/events/chief-in-tech-summit", icon: Globe, description: "Annual leadership summit" },
          { label: "Speakers", href: "/speakers", icon: Mic, description: "Featured speakers" },
        ]
      },
    ],
    routePatterns: [
      "/events", "/speakers",
    ],
  },

  membership: {
    label: "Membership",
    href: "/membership",
    icon: Star,
    description: "Plans, benefits, and ecosystem",
    subNavItems: [
      { label: "Plans", href: "/membership", icon: Star },
      { label: "Insider", href: "/membership/insider", icon: Eye },
      { label: "Executive", href: "/membership/executive", icon: Award },
      { label: "Boardroom", href: "/membership/boardroom", icon: Target },
      { label: "Ecosystem", href: "/ecosystem", icon: Globe },
      { label: "Brand Hub", href: "/brand-hub", icon: Building2 },
      { label: "Perks", href: "/network/perks", icon: ShoppingBag },
    ],
    hamburgerGroups: [
      {
        title: "Plans",
        items: [
          { label: "Membership Overview", href: "/membership", icon: Star, description: "Explore membership tiers" },
          { label: "Insider", href: "/membership/insider", icon: Eye, description: "Premium content & community" },
          { label: "Executive", href: "/membership/executive", icon: Award, description: "Strategic connections" },
          { label: "Boardroom", href: "/membership/boardroom", icon: Target, description: "Industry leadership" },
          { label: "Upgrade", href: "/membership/upgrade", icon: TrendingUp, description: "Compare & upgrade plans" },
        ]
      },
      {
        title: "Benefits",
        items: [
          { label: "Partner Perks", href: "/network/perks", icon: ShoppingBag, description: "Exclusive partner benefits" },
          { label: "Brand Hub", href: "/brand-hub", icon: Building2, description: "Media kits and assets" },
        ]
      },
      {
        title: "Ecosystem",
        items: [
          { label: "Ecosystem Map", href: "/ecosystem", icon: Globe, description: "Tech stakeholder directory" },
        ]
      },
    ],
    routePatterns: [
      "/membership", "/pricing", "/ecosystem", "/brand-hub", "/network/perks", "/contributor",
    ],
  },

  summit: {
    label: "Summit",
    href: "/events/chief-in-tech-summit",
    icon: Presentation,
    description: "Annual tech leadership conference",
    subNavItems: [
      { label: "Tracks & Topics", href: "/events/chief-in-tech-summit#tracks", icon: LayoutList },
      { label: "Speakers", href: "/events/chief-in-tech-summit#speakers", icon: Mic },
      { label: "Executive Forums", href: "/events/executive-forum", icon: Calendar },
      { label: "Testimonials", href: "/events/chief-in-tech-summit#testimonials", icon: Quote },
      { label: "Register", href: "/events/chief-in-tech-summit#register", icon: Ticket },
      { label: "Partners", href: "/partners", icon: Building2 },
      { label: "Shop", href: "/summit/shop", icon: ShoppingBag },
    ],
    hamburgerGroups: [
      {
        title: "Event Info",
        items: [
          { label: "Tracks & Topics", href: "/events/chief-in-tech-summit#tracks", icon: LayoutList, description: "Session tracks and topics" },
          { label: "Speakers", href: "/events/chief-in-tech-summit#speakers", icon: Mic, description: "Featured keynote speakers" },
          { label: "Executive Forums", href: "/events/executive-forum", icon: Calendar, description: "Exclusive networking forums" },
          { label: "Testimonials", href: "/events/chief-in-tech-summit#testimonials", icon: Quote, description: "What attendees say" },
          { label: "Register", href: "/events/chief-in-tech-summit#register", icon: Ticket, description: "Reserve your spot" },
        ]
      },
      {
        title: "More",
        items: [
          { label: "Partners & Sponsors", href: "/partners", icon: Building2, description: "Our partners" },
          { label: "Summit Shop", href: "/summit/shop", icon: ShoppingBag, description: "Summit merchandise" },
        ]
      },
    ],
    routePatterns: [
      "/events/chief-in-tech-summit", "/summit",
      "/partners",
    ],
  },
};

export const moreMenuItems: NavMenuItem[] = [
  { label: "About", href: "/about", icon: Users, description: "About Chief in Tech", keywords: ["mission", "vision", "who", "story", "team"] },
  { label: "The Book", href: "/book", icon: BookOpen, description: "Break the Silicon Ceiling", keywords: ["book", "silicon", "ceiling", "read", "publication"] },
  { label: "Contact", href: "/contact", icon: FileText, description: "Get in touch", keywords: ["contact", "reach", "email", "support", "help"] },
];

export const mobileBottomNavItems = [
  { label: "Home", href: "/", icon: Home },
  { label: "Discover", href: "/media", icon: Compass },
  { label: "Network", href: "/directory", icon: Users },
  { label: "Capital", href: "/capital", icon: Landmark },
  { label: "Profile", href: "/profile", icon: User },
];

export function getActiveSection(pathname: string): Section | null {
  const path = pathname.split("?")[0].split("#")[0];
  
  const sectionOrder: Section[] = ["summit", "events", "capital", "programs", "membership", "network", "discover"];
  
  for (const key of sectionOrder) {
    const config = sections[key];
    for (const pattern of config.routePatterns) {
      if (path === pattern || path.startsWith(pattern + "/")) {
        return key;
      }
    }
  }
  return null;
}

export function getSubNavItems(
  section: Section,
  userRole?: string | null,
  activeRoles?: string[],
  userInterestSlugs?: string[],
): SubNavItem[] {
  const config = sections[section];
  const roles = activeRoles && activeRoles.length > 0 ? activeRoles : userRole ? [userRole] : [];

  let items = config.subNavItems.filter((item) => {
    if (!item.requiredRole) return true;
    return roles.includes(item.requiredRole);
  });

  if (roles.length === 0 && (!userInterestSlugs || userInterestSlugs.length === 0)) {
    return items;
  }

  const primaryRole = userRole || roles[0] || null;
  const secondaryRoles = primaryRole ? roles.filter(r => r !== primaryRole) : roles;
  const interests = new Set(userInterestSlugs || []);

  const hasInterestMatch = (item: SubNavItem): boolean =>
    !!item.interestTags && item.interestTags.some(tag => interests.has(tag));

  const interestMatchCount = (item: SubNavItem): number =>
    item.interestTags ? item.interestTags.filter(tag => interests.has(tag)).length : 0;

  const isUntagged = (item: SubNavItem): boolean =>
    !item.requiredRole && !item.priorityRole && !item.interestTags;

  const firstItem = items[0];
  const hasStableFirst = firstItem && isUntagged(firstItem);
  const sortableItems = hasStableFirst ? items.slice(1) : items;

  const promoted: { item: SubNavItem; score: number }[] = [];
  const unpromoted: SubNavItem[] = [];

  for (const item of sortableItems) {
    const matchesInterest = hasInterestMatch(item);
    const matchesPrimary = !!primaryRole && (item.priorityRole === primaryRole || item.requiredRole === primaryRole);
    const matchesSecondary = secondaryRoles.some(r => item.priorityRole === r || item.requiredRole === r);

    let score = 0;
    if (matchesInterest && matchesPrimary) {
      score = 600 + interestMatchCount(item) * 10 - (item.priority || 0);
    } else if (matchesPrimary) {
      score = 500 - (item.priority || 0);
    } else if (matchesInterest && matchesSecondary) {
      score = 400 + interestMatchCount(item) * 10 - (item.priority || 0);
    } else if (matchesSecondary) {
      score = 300 - (item.priority || 0);
    } else if (matchesInterest) {
      score = 200 + interestMatchCount(item) * 10;
    }

    if (score > 0) {
      promoted.push({ item, score });
    } else {
      unpromoted.push(item);
    }
  }

  promoted.sort((a, b) => b.score - a.score);
  const sorted = [...promoted.map(s => s.item), ...unpromoted];

  return hasStableFirst ? [firstItem, ...sorted] : sorted;
}

interface MembershipNavUser {
  isPremium?: boolean;
  platformMembershipTier?: string | null;
  contributorTier?: string | null;
  activeRoles?: string[];
}

const tierLabels: Record<string, string> = {
  insider: "Insider",
  executive: "Executive",
  boardroom: "Boardroom",
};

const tierIcons: Record<string, LucideIcon> = {
  insider: Eye,
  executive: Award,
  boardroom: Target,
};

export function getMembershipNavItems(user?: MembershipNavUser | null): SubNavItem[] {
  if (!user) {
    return [
      { label: "Plans", href: "/membership", icon: Star },
      { label: "Insider", href: "/membership/insider", icon: Eye },
      { label: "Executive", href: "/membership/executive", icon: Award },
      { label: "Boardroom", href: "/membership/boardroom", icon: Target },
      { label: "Upgrade", href: "/membership/upgrade", icon: TrendingUp },
      { label: "Ecosystem", href: "/ecosystem", icon: Globe },
    ];
  }

  const tier = user.platformMembershipTier;
  const items: SubNavItem[] = [];

  if (tier && tierLabels[tier]) {
    items.push({ label: "My Membership", href: "/account#billing", icon: Star });
    items.push({
      label: tierLabels[tier],
      href: `/membership/${tier}`,
      icon: tierIcons[tier],
      badge: "Current",
    });
    if (tier !== "boardroom") {
      items.push({ label: "Upgrade", href: "/membership/upgrade", icon: TrendingUp });
    }
  } else {
    items.push({ label: "Plans", href: "/membership", icon: Star });
    items.push({ label: "Upgrade", href: "/membership/upgrade", icon: TrendingUp, badge: "Recommended" });
  }

  items.push({ label: "Perks", href: "/network/perks", icon: ShoppingBag });
  items.push({ label: "Ecosystem", href: "/ecosystem", icon: Globe });
  items.push({ label: "Brand Hub", href: "/brand-hub", icon: Building2 });

  if (user.contributorTier && user.contributorTier !== "reader") {
    items.push({ label: "Contributor", href: "/contributor", icon: PenTool });
  }

  return items;
}

export function isPathActive(pathname: string, href: string): boolean {
  const cleanPath = pathname.split("?")[0].split("#")[0];
  
  if (href.includes('#')) {
    const basePath = href.split('#')[0];
    return basePath ? cleanPath === basePath || cleanPath.startsWith(basePath + '/') : false;
  }
  
  const currentUrl = new URL(window.location.href);
  
  if (href.includes('?')) {
    const basePath = href.split('?')[0];
    const hrefParams = new URLSearchParams(href.split('?')[1] || '');
    
    if (cleanPath !== basePath && !cleanPath.startsWith(basePath + '/')) {
      return false;
    }
    
    const entries = Array.from(hrefParams.entries());
    for (let i = 0; i < entries.length; i++) {
      const [key, value] = entries[i];
      if (currentUrl.searchParams.get(key) !== value) {
        return false;
      }
    }
    return true;
  }
  
  if (cleanPath === href || cleanPath.startsWith(href + '/')) {
    const l3 = level3Config[href];
    if (l3 && l3.some(item => item.href.includes('?'))) {
      const filterKeys = new Set<string>();
      l3.forEach(item => {
        if (item.href.includes('?')) {
          new URLSearchParams(item.href.split('?')[1]).forEach((_, key) => filterKeys.add(key));
        }
      });
      for (const key of Array.from(filterKeys)) {
        if (currentUrl.searchParams.has(key)) return false;
      }
    }
    return true;
  }
  
  return false;
}

export type Pillar = "media" | "network" | "community" | "capital" | "book" | "summit";

export const mediaMenuSections: NavMenuSection[] = sections.discover.hamburgerGroups;
export const networkMenuSections: NavMenuSection[] = sections.network.hamburgerGroups;
export const communityMenuSections: NavMenuSection[] = sections.programs.hamburgerGroups;
export const capitalMenuSections: NavMenuSection[] = sections.capital.hamburgerGroups;
export const bookMenuSections: NavMenuSection[] = [
  {
    title: "Chief in Tech Book",
    items: [
      { label: "About the Book", href: "/book", icon: BookOpen, description: "Break the Silicon Ceiling" },
      { label: "What You'll Learn", href: "/book#learn", icon: Lightbulb, description: "Key insights and takeaways" },
      { label: "50 Leaders Featured", href: "/book#leaders", icon: Trophy, description: "Leaders profiled in the book" },
    ]
  },
];
export const summitMenuSections: NavMenuSection[] = [
  {
    title: "Event Info",
    items: [
      { label: "Summit Overview", href: "/events/chief-in-tech-summit", icon: Calendar, description: "Chief in Tech Summit 2026" },
      { label: "Speakers", href: "/speakers", icon: Mic, description: "Featured keynote speakers" },
    ]
  },
];

export const pillarMenuSections: Record<Pillar, NavMenuSection[]> = {
  media: mediaMenuSections,
  network: networkMenuSections,
  community: communityMenuSections,
  capital: capitalMenuSections,
  book: bookMenuSections,
  summit: summitMenuSections,
};

export interface MediaQuickLink {
  label: string;
  href: string;
}

export const mediaQuickLinks: MediaQuickLink[] = [
  { label: "AI Leadership", href: "/media?category=ai_leadership" },
  { label: "Fundraising", href: "/media?category=fundraising" },
  { label: "Boards", href: "/media?category=boards" },
  { label: "Trending", href: "/media?sort=trending" },
];

export const MAX_VISIBLE_NAV_ITEMS = 5;

export const level3Config: Record<string, SubNavItem[]> = {
  "/media": [
    { label: "All", href: "/media", icon: Sparkles },
    { label: "AI Leadership", href: "/media?category=ai_leadership", icon: Brain },
    { label: "Fundraising", href: "/media?category=fundraising", icon: Rocket },
    { label: "Boards", href: "/media?category=boards", icon: LayoutGrid },
    { label: "Sustainability", href: "/media?category=sustainability", icon: Globe },
    { label: "Culture", href: "/media?category=culture", icon: Heart },
    { label: "Emerging Tech", href: "/media?category=emerging_tech", icon: Lightbulb },
  ],
  "/media/articles": [
    { label: "All", href: "/media/articles", icon: FileText },
    { label: "AI Leadership", href: "/media/articles?category=ai_leadership", icon: Brain },
    { label: "Fundraising", href: "/media/articles?category=fundraising", icon: Rocket },
    { label: "Boards", href: "/media/articles?category=boards", icon: LayoutGrid },
    { label: "Culture", href: "/media/articles?category=culture", icon: Heart },
    { label: "Sustainability", href: "/media/articles?category=sustainability", icon: Globe },
    { label: "Emerging Tech", href: "/media/articles?category=emerging_tech", icon: Lightbulb },
  ],
  "/media/topics": [
    { label: "All Topics", href: "/media/topics", icon: LayoutGrid },
  ],
  "/media/daily-brief": [
    { label: "Today", href: "/media/daily-brief", icon: Brain },
    { label: "This Week", href: "/media/daily-brief?view=week", icon: Calendar },
    { label: "Archive", href: "/media/daily-brief?view=archive", icon: Clock },
  ],
  "/media/news": [
    { label: "Overview", href: "/media/news", icon: Newspaper },
    { label: "Industry News", href: "/media/news/industry-news", icon: Newspaper },
    { label: "Funding News", href: "/media/news/funding", icon: TrendingUp },
    { label: "Executive Moves", href: "/media/news/executive-moves", icon: Briefcase },
  ],
  "/events": [
    { label: "Upcoming", href: "/events", icon: Calendar },
    { label: "Executive Forums", href: "/events/executive-forum", icon: Calendar },
    { label: "Speakers", href: "/speakers", icon: Mic },
  ],

  "/leaders": [
    { label: "Current Year", href: "/leaders", icon: Trophy },
    { label: "Nominate", href: "/nominate", icon: Award },
  ],
  "/capital": [
    { label: "For Founders", href: "/founder/workspace", icon: Rocket },
    { label: "For Investors", href: "/capital/dealroom", icon: HandshakeIcon },
    { label: "Fund", href: "/capital/fund", icon: DollarSign },
    { label: "Challenges", href: "/challenges", icon: Lightbulb },
  ],
  "/chief-in-tech-magazine": [
    { label: "Current Issue", href: "/chief-in-tech-magazine", icon: BookMarked },
    { label: "Past Issues", href: "/magazine", icon: BookMarked },
    { label: "Submit", href: "/contribute", icon: FileText },
  ],
  "/membership": [
    { label: "Overview", href: "/membership", icon: Star },
    { label: "Insider", href: "/membership/insider", icon: Eye },
    { label: "Executive", href: "/membership/executive", icon: Award },
    { label: "Boardroom", href: "/membership/boardroom", icon: Target },
    { label: "Upgrade", href: "/membership/upgrade", icon: TrendingUp },
  ],
  "/circles": [
    { label: "Browse", href: "/circles", icon: CircleDot },
    { label: "My Circles", href: "/circles?tab=my", icon: Users },
    { label: "Create", href: "/circles?tab=create", icon: Plus },
  ],
  "/jobs": [
    { label: "All Jobs", href: "/jobs", icon: Briefcase },
    { label: "Executive", href: "/jobs?level=executive", icon: Crown },
    { label: "Board Seats", href: "/board-opportunities", icon: Award },
  ],
};

export function getActiveSubItem(pathname: string, section: Section): SubNavItem | null {
  const config = sections[section];
  const path = pathname.split("?")[0].split("#")[0];

  let bestMatch: SubNavItem | null = null;
  let bestLength = 0;

  for (const item of config.subNavItems) {
    const itemPath = item.href.split("?")[0].split("#")[0];
    if (path === itemPath || path.startsWith(itemPath + "/")) {
      if (itemPath.length > bestLength) {
        bestLength = itemPath.length;
        bestMatch = item;
      }
    }
  }

  return bestMatch;
}

export function getLevel3Items(subItemHref: string): SubNavItem[] | null {
  const basePath = subItemHref.split("?")[0].split("#")[0];
  return level3Config[basePath] || null;
}

export function getNavigationDepth(pathname: string, section: Section | null): 0 | 1 | 2 {
  if (!section) return 0;

  const config = sections[section];
  const activeSubItem = getActiveSubItem(pathname, section);
  if (activeSubItem) {
    const subItemBase = activeSubItem.href.split("?")[0].split("#")[0];
    if (subItemBase === config.href) {
      return 1;
    }
    return 2;
  }

  return 1;
}
